import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Trash2, Download, CheckCircle2, Mail } from "lucide-react";
import { api, errMsg, fmtDate, fmtMoney } from "../../lib/api";
import { DataTable, Td, StatusBadge, EmptyState, ActionButton } from "../common";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ProformaList = () => {
  const [rows, setRows] = useState(null);
  const [emailing, setEmailing] = useState(null);

  const load = () => api.get("/proformas").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const emailProforma = async (p) => {
    if (!window.confirm(`Email Proforma ${p.number} to ${p.recipient?.email || "the requester"}?`)) return;
    setEmailing(p.id);
    try {
      const r = await api.post(`/proformas/${p.id}/email`);
      toast.success(`Emailed to ${r.data.recipient}`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setEmailing(null); }
  };

  const finalize = async (p) => {
    try {
      await api.patch(`/proformas/${p.id}`, { status: p.status === "final" ? "draft" : "final" });
      toast.success(p.status === "final" ? "Moved back to Draft" : "Marked Final");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete Proforma ${p.number}? This cannot be undone.`)) return;
    try { await api.delete(`/proformas/${p.id}`); toast.success(`${p.number} deleted`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mb-10" data-testid="proforma-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">Proforma Sales Orders</p>
          <p className="text-sm text-slate-400">Created from Rental Dispatch tickets. Not an invoice — the ticket stays open.</p>
        </div>
      </div>
      {rows === null ? (
        <EmptyState message="Loading proformas…" testId="proforma-loading-list" />
      ) : rows.length === 0 ? (
        <EmptyState message="No Proforma Sales Orders yet — create one from a Rental Dispatch ticket." testId="proforma-empty" />
      ) : (
        <DataTable
          testId="proforma-table"
          columns={["Proforma", "For Ticket", "Customer", "Total", "Status", "Created", "Actions"]}
          rows={rows}
          renderRow={(p) => (
            <tr key={p.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`proforma-row-${p.id}`}>
              <Td className="font-mono text-amber-400">{p.number}</Td>
              <Td className="font-mono text-slate-300">{p.dispatch_number}</Td>
              <Td className="font-medium text-slate-100">{p.customer_name}{p.stage && <span className="ml-2 text-[10px] font-mono text-slate-500">Stage {p.stage}</span>}{p.recipient?.email && <div data-testid={`proforma-recipient-${p.id}`} className="text-[10px] text-slate-500">{p.recipient.email}{p.email_status === "sent" && <span className="text-emerald-500"> · sent ✓</span>}{p.email_status === "failed" && <span className="text-red-500"> · failed</span>}</div>}</Td>
              <Td className="font-mono text-emerald-400">{fmtMoney(p.total)}</Td>
              <Td><StatusBadge status={p.status} testId={`proforma-status-${p.id}`} /></Td>
              <Td className="text-slate-500 text-xs">{fmtDate(p.created_at)}</Td>
              <Td>
                <div className="flex items-center gap-1">
                  <a data-testid={`proforma-pdf-${p.id}`} href={`${BACKEND}/api/proformas/${p.id}/pdf`} target="_blank" rel="noreferrer" className="p-1.5 text-slate-400 hover:text-amber-400" title="Download PDF"><Download size={15} /></a>
                  <button data-testid={`proforma-email-${p.id}`} disabled={!p.recipient?.email || emailing === p.id} onClick={() => emailProforma(p)} className="p-1.5 text-slate-400 hover:text-sky-400 disabled:opacity-40 disabled:cursor-not-allowed" title={p.recipient?.email ? `Email PDF to ${p.recipient.email}` : "No requester email — pick one when creating the Proforma"}><Mail size={15} /></button>
                  <button data-testid={`proforma-finalize-${p.id}`} onClick={() => finalize(p)} className="p-1.5 text-slate-400 hover:text-emerald-400" title={p.status === "final" ? "Revert to Draft" : "Mark Final"}><CheckCircle2 size={15} /></button>
                  <button data-testid={`proforma-delete-${p.id}`} onClick={() => remove(p)} className="p-1.5 text-slate-400 hover:text-red-400" title="Delete"><Trash2 size={15} /></button>
                </div>
              </Td>
            </tr>
          )}
        />
      )}
    </div>
  );
};
